import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LogOutIcon, PanelLeftIcon } from 'lucide-react';
import Logo from '@/assets/logo.png';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { isClubUser } from '@/services/authSession';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import ClubUiProviders from '../providers/ClubUiProviders';

const registerSchema = z
    .object({
        password: z.string().min(8, 'Mínimo de 8 caracteres'),
        confirmPassword: z.string().min(1, 'Confirme a senha'),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'As senhas devem ser iguais',
        path: ['confirmPassword'],
    });

const loginSchema = z.object({
    password: z.string().min(1, 'Informe a senha'),
});

const roleLabels = {
    admin: 'Administrador',
    manager: 'Gerente',
    user: 'Usuário',
    owner: 'Proprietário',
};

function ClubInviteSummary({ invite }) {
    return (
        <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-3">
            {invite.club?.logo ? (
                <img
                    src={invite.club.logo}
                    alt=""
                    className="size-12 shrink-0 rounded-lg object-contain bg-background"
                />
            ) : (
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-background text-sm font-semibold text-muted-foreground">
                    {(invite.club?.name || 'C').slice(0, 2).toUpperCase()}
                </div>
            )}
            <div className="min-w-0 space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Clube do convite
                </p>
                <p className="truncate text-base font-semibold">{invite.club?.name || 'Clube'}</p>
                <p className="text-sm text-muted-foreground">
                    Cargo: <strong>{roleLabels[invite.role] || invite.role}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                    Convidado: <strong>{invite.email}</strong>
                </p>
            </div>
        </div>
    );
}

function InviteAcceptForm() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { Login, LogOut, user } = useAuth();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('register');

    const registerForm = useForm({
        resolver: zodResolver(registerSchema),
        defaultValues: { password: '', confirmPassword: '' },
    });
    const loginForm = useForm({
        resolver: zodResolver(loginSchema),
        defaultValues: { password: '' },
    });

    const sessionEmail = String(user?.userName || '').trim().toLowerCase();
    const inviteEmail = String(invite?.email || '').trim().toLowerCase();
    const isLoggedIn = Boolean(user?.id);
    const inviteIsForSomeoneElse = useMemo(() => {
        if (!isLoggedIn || !inviteEmail) return false;
        return sessionEmail !== inviteEmail;
    }, [isLoggedIn, sessionEmail, inviteEmail]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await server.get(`club/invites/${token}`);
                if (cancelled) return;
                const payload = data?.data;
                setInvite(payload);
                setMode(payload?.userExists ? 'login' : 'register');
            } catch (error) {
                if (!cancelled) setErrors(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- carregar só quando o token muda
    }, [token]);

    const finishAccept = async body => {
        const { data } = await server.post(`club/invites/${token}/accept`, body);
        const result = data?.data;
        await Login({
            token: result.token,
            refreshtoken: result.refreshToken,
            user: result.user,
            club: result.club,
        });
        enqueueSnackbar('Convite aceito. Bem-vindo!');
        navigate('/clube/dashboard', { replace: true });
    };

    const onRegister = async values => {
        try {
            await finishAccept({
                mode: 'register',
                password: values.password,
            });
        } catch (error) {
            setErrors(error);
        }
    };

    const onLogin = async values => {
        try {
            await finishAccept({
                mode: 'login',
                password: values.password,
            });
        } catch (error) {
            setErrors(error);
        }
    };

    const handleLogout = () => {
        LogOut();
    };

    if (loading) {
        return (
            <div className="flex min-h-svh items-center justify-center">
                <Spinner className="size-8" />
            </div>
        );
    }

    if (!invite) {
        return (
            <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-4 p-6">
                <Alert variant="destructive">
                    <AlertTitle>Convite inválido</AlertTitle>
                    <AlertDescription>Este link não é válido ou expirou.</AlertDescription>
                </Alert>
                <Button render={<Link to="/clube/login" />}>Ir para o login</Button>
            </div>
        );
    }

    if (!invite.canAccept) {
        return (
            <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-4 p-6">
                <img src={Logo} alt="Eurobem" className="h-10 w-auto self-start object-contain" />
                <ClubInviteSummary invite={invite} />
                <Alert>
                    <AlertTitle>Convite indisponível</AlertTitle>
                    <AlertDescription>
                        Status: {invite.status}. Solicite um novo convite ao proprietário do clube.
                    </AlertDescription>
                </Alert>
                <Button render={<Link to="/clube/login" />}>Ir para o login</Button>
            </div>
        );
    }

    if (inviteIsForSomeoneElse) {
        return (
            <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-6 p-6">
                <img src={Logo} alt="Eurobem" className="h-10 w-auto self-start object-contain" />

                <Card>
                    <CardHeader>
                        <CardTitle>Este convite não é para você</CardTitle>
                        <CardDescription>
                            Você está logado com outra conta. Para aceitar, saia e use o e-mail do convite.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <ClubInviteSummary invite={invite} />

                        <Separator />

                        <div className="space-y-1 text-sm">
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Sessão atual
                            </p>
                            <p>
                                Conta logada: <strong>{user?.userName || '—'}</strong>
                            </p>
                            {user?.role ? (
                                <p className="text-muted-foreground">
                                    Cargo nesta sessão: {roleLabels[user.role] || user.role}
                                </p>
                            ) : null}
                        </div>

                        <Alert variant="destructive">
                            <AlertTitle>E-mails diferentes</AlertTitle>
                            <AlertDescription>
                                O convite é para <strong>{invite.email}</strong>, mas você está como{' '}
                                <strong>{user?.userName}</strong>.
                            </AlertDescription>
                        </Alert>

                        <div className="flex flex-col gap-2 sm:flex-row">
                            {isClubUser(user) ? (
                                <Button
                                    className="flex-1"
                                    variant="outline"
                                    render={<Link to="/clube/dashboard" />}
                                >
                                    <PanelLeftIcon className="size-4" />
                                    Voltar ao painel
                                </Button>
                            ) : (
                                <Button className="flex-1" variant="outline" render={<Link to="/" />}>
                                    Voltar
                                </Button>
                            )}
                            <Button className="flex-1" variant="destructive" onClick={handleLogout}>
                                <LogOutIcon className="size-4" />
                                Sair
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-6 p-6">
            <img src={Logo} alt="Eurobem" className="h-10 w-auto self-start object-contain" />

            <Card>
                <CardHeader>
                    <CardTitle>Aceitar convite</CardTitle>
                    <CardDescription>
                        Confirme os dados abaixo e continue para entrar no clube.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <ClubInviteSummary invite={invite} />

                    <Tabs value={mode} onValueChange={setMode}>
                        <TabsList className="w-full">
                            <TabsTrigger value="register" className="flex-1" disabled={invite.userExists}>
                                Criar conta
                            </TabsTrigger>
                            <TabsTrigger value="login" className="flex-1">
                                Já tenho conta
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="register" className="mt-4">
                            <form
                                onSubmit={registerForm.handleSubmit(onRegister)}
                                className="flex flex-col gap-4"
                            >
                                <FieldGroup>
                                    <Field data-invalid={Boolean(registerForm.formState.errors.password)}>
                                        <FieldLabel>Senha</FieldLabel>
                                        <Controller
                                            name="password"
                                            control={registerForm.control}
                                            render={({ field }) => (
                                                <Input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldError>
                                            {registerForm.formState.errors.password?.message}
                                        </FieldError>
                                    </Field>
                                    <Field
                                        data-invalid={Boolean(
                                            registerForm.formState.errors.confirmPassword,
                                        )}
                                    >
                                        <FieldLabel>Confirmar senha</FieldLabel>
                                        <Controller
                                            name="confirmPassword"
                                            control={registerForm.control}
                                            render={({ field }) => (
                                                <Input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldError>
                                            {registerForm.formState.errors.confirmPassword?.message}
                                        </FieldError>
                                    </Field>
                                </FieldGroup>
                                <Button type="submit" disabled={registerForm.formState.isSubmitting}>
                                    {registerForm.formState.isSubmitting ? (
                                        <Spinner className="size-4" />
                                    ) : (
                                        'Criar conta e aceitar'
                                    )}
                                </Button>
                            </form>
                        </TabsContent>

                        <TabsContent value="login" className="mt-4">
                            <form onSubmit={loginForm.handleSubmit(onLogin)} className="flex flex-col gap-4">
                                <FieldGroup>
                                    <Field>
                                        <FieldLabel>E-mail</FieldLabel>
                                        <Input value={invite.email} disabled />
                                    </Field>
                                    <Field data-invalid={Boolean(loginForm.formState.errors.password)}>
                                        <FieldLabel>Senha</FieldLabel>
                                        <Controller
                                            name="password"
                                            control={loginForm.control}
                                            render={({ field }) => (
                                                <Input
                                                    type="password"
                                                    autoComplete="current-password"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldError>
                                            {loginForm.formState.errors.password?.message}
                                        </FieldError>
                                    </Field>
                                </FieldGroup>
                                <Button type="submit" disabled={loginForm.formState.isSubmitting}>
                                    {loginForm.formState.isSubmitting ? (
                                        <Spinner className="size-4" />
                                    ) : (
                                        'Entrar e aceitar'
                                    )}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
}

export default function ClubInviteAccept() {
    return (
        <ClubUiProviders rootId="club-invite-root">
            <InviteAcceptForm />
        </ClubUiProviders>
    );
}
